import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbCalendar, TbStar } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { type CalendarListPayload, parseCalendarListPayload } from "./calendarListSchema";

interface CalendarListRendererProps {
  block: StreamingBlock;
}

export const CalendarListRenderer = (props: CalendarListRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<CalendarListPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseCalendarListPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("calendar_list_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().calendars.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbCalendar class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{rt.t("calendar_list_card.title")}</span>
                <span class="ml-auto text-xs text-muted-text">
                  {data().calendars.length === 1
                    ? rt.t("calendar_list_card.count_one")
                    : rt.t("calendar_list_card.count", { count: String(data().calendars.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().calendars}>
                  {(calendar) => (
                    <li class="flex items-center gap-3 px-3 py-2">
                      <div class="flex min-w-0 flex-1 items-center gap-2">
                        <h3 class="truncate text-sm font-medium text-primary-text">{calendar.summary}</h3>
                        <Show when={calendar.primary}>
                          <span class="inline-flex shrink-0 items-center gap-1 rounded-full bg-panel-hover px-2 py-0.5 text-xs text-muted-text">
                            <TbStar class="size-3 shrink-0" />
                            {rt.t("calendar_list_card.primary")}
                          </span>
                        </Show>
                      </div>
                      <Show when={calendar.accessRole}>
                        <span class="shrink-0 text-xs text-muted-text">
                          {rt.t("calendar_list_card.access_role", { role: calendar.accessRole })}
                        </span>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
